import { Action } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { UserModel } from '../models/UserModel';
import { getUserByToken } from './AuthCRUD';
import { failureToast } from '../../../support/utils';
import { CookieComponent } from '../../../../_start/assets/ts/components/_CookieComponent';
import { WEB_DOMAIN } from '../../../support/api';
import { RootState } from '../../../../setup';

export interface ActionWithPayload<T> extends Action {
  payload?: T;
}

export const actionTypes = {
  Login: '[Login] Action',
  Logout: '[Logout] Action',
  Register: '[Register] Action',
  UserRequested: '[Request User] Action',
  UserLoaded: '[Load User] Auth API',
  SetUser: '[Set User] Action',
  CheckUser: '[Check User] Action',
};

const initialAuthState: IAuthState = {
  user: undefined,
  accessToken: undefined,
};

export interface IAuthState {
  user?: UserModel;
  accessToken?: string;
}

export const reducer = persistReducer(
  { storage, key: 'v100-demo1-auth', whitelist: ['user', 'accessToken'] },
  (
    state: IAuthState = initialAuthState,
    action: ActionWithPayload<IAuthState>
  ) => {
    switch (action.type) {
      case actionTypes.Login: {
        const accessToken = action.payload?.accessToken;
        return { accessToken, user: undefined };
      }

      case actionTypes.Register: {
        const accessToken = action.payload?.accessToken;
        return { accessToken, user: undefined };
      }

      case actionTypes.Logout: {
        return initialAuthState;
      }

      case actionTypes.UserRequested: {
        return { ...state, user: undefined };
      }

      case actionTypes.UserLoaded: {
        const user = action.payload?.user;
        return { ...state, user };
      }

      case actionTypes.SetUser: {
        const user = action.payload?.user;
        return { ...state, user };
      }

      default:
        return state;
    }
  }
);

export const actions = {
  login: (accessToken: string) => ({
    type: actionTypes.Login,
    payload: { accessToken },
  }),
  register: (accessToken: string) => ({
    type: actionTypes.Register,
    payload: { accessToken },
  }),
  logout: () => ({ type: actionTypes.Logout }),
  requestUser: () => ({
    type: actionTypes.UserRequested,
  }),
  fulfillUser: (user: UserModel) => ({
    type: actionTypes.UserLoaded,
    payload: { user },
  }),
  setUser: (user: UserModel) => ({
    type: actionTypes.SetUser,
    payload: { user },
  }),
  checkUser: () => ({ type: actionTypes.CheckUser }),
};

// SELECTOR
const accessToken = (state: RootState) => state.auth.accessToken;

export function* saga() {
  yield takeLatest(actionTypes.CheckUser, function* checkUserSaga() {
    try {
      const [user]: [UserModel] = yield all([call(getUserByToken)]);
      yield put(actions.fulfillUser(user));
    } catch (error) {
      yield call(failureToast, error);
      yield put(actions.logout());
    }
  });

  yield takeLatest(actionTypes.Login, function* loginSaga() {
    yield put(actions.requestUser());
  });

  yield takeLatest(actionTypes.Register, function* registerSaga() {
    yield put(actions.requestUser());
  });

  yield takeLatest(actionTypes.UserRequested, function* userRequested() {
    try {
      const [user]: [UserModel] = yield all([call(getUserByToken)]);

      const access_token: string = yield select(accessToken);

      CookieComponent.set('access_token', access_token, {
        // Host-only cookie prevents collisions with verbonden.nl website sessions.
        path: '/',
      });

      yield put(actions.fulfillUser(user));
    } catch (error) {
      yield call(failureToast, error);
    }
  });

  // eslint-disable-next-line require-yield
  yield takeLatest(actionTypes.Logout, function* logoutSaga() {
    // Remove current host cookie.
    CookieComponent.set('access_token', '', {
      path: '/',
      'max-age': -1,
    });

    // Remove legacy shared-domain cookie from previous versions.
    if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
      CookieComponent.set('access_token', '', {
        path: '/',
        domain: `.${WEB_DOMAIN}`,
        'max-age': -1,
      });
    }
  });
}
